import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { useAuth } from '../../auth/authContext.js';
import { getImpersonatedMemberId, setImpersonatedMemberId } from '../../auth/impersonation.js';
import { Button } from '../ui/Button.js';
import { Spinner } from '../ui/Spinner.js';
import { MaintenanceNotice } from './MaintenanceNotice.js';
import { SandboxNotice } from './SandboxNotice.js';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { UpdateBanner } from './UpdateBanner.js';

function AuthErrorPanel(): JSX.Element {
  const isImpersonating = getImpersonatedMemberId() !== null;
  return (
    <div className="mx-auto max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
      <h2 className="text-base font-semibold text-amber-900">We couldn&apos;t load your profile</h2>
      <p className="mt-2 text-sm text-amber-800">
        Your sign-in succeeded, but the API could not resolve your account. You may not yet be
        provisioned as a member, or your session may have expired. Contact an administrator if this
        persists.
      </p>
      {isImpersonating ? (
        <div className="mt-4">
          <p className="text-sm text-amber-800">
            You are currently impersonating another member. If this view is broken, stop
            impersonating to return to your own account.
          </p>
          <Button
            variant="secondary"
            className="mt-3"
            onClick={() => {
              setImpersonatedMemberId(null);
              window.location.reload();
            }}
          >
            Stop impersonating and reload
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Persistent strip shown while an admin is impersonating another member. */
function ImpersonationBanner(): JSX.Element | null {
  const { impersonator, principal, stopImpersonation } = useAuth();
  if (!impersonator) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
      <span>
        Viewing PSA as <strong>{principal?.displayName ?? 'another member'}</strong> (impersonated
        by {impersonator.displayName}).
      </span>
      <Button variant="secondary" className="h-7 px-2 py-0 text-xs" onClick={stopImpersonation}>
        Stop impersonating
      </Button>
    </div>
  );
}

export function AppLayout(): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isLoading, isError } = useAuth();

  return (
    <div className="flex h-full min-h-screen bg-slate-50">
      <aside className="app-sidebar hidden w-64 shrink-0 bg-[var(--sidebar-bg)] lg:block">
        <Sidebar />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-slate-900/50 dark:bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="app-sidebar absolute inset-y-0 left-0 w-64 bg-[var(--sidebar-bg)]">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <UpdateBanner />
        <SandboxNotice />
        <MaintenanceNotice />
        <ImpersonationBanner />
        <Topbar onMenuClick={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {isError ? (
            <AuthErrorPanel />
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
              <Spinner className="h-6 w-6" />
              <span className="text-sm">Loading your workspace…</span>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
